/**
 * WorkspaceRiskBrief — "Where to focus first" Steering-Committee brief (Iter 36.b).
 *
 * Composes the heat-map (Iter 29 + 35.b enhancement density), the business-rule
 * inventory (Iter 36), and the modified-standard hot-spots into a single
 * top-10 ranked page consultants can copy/paste into a slide.
 *
 * Auto-hides sections with no data. A "Copy as markdown" button serialises
 * the visible content into a slide-ready markdown table.
 */
import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import {
  CircleNotchIcon as Spinner,
  ChartBarIcon as ChartBar,
  ClipboardTextIcon as ClipboardText,
  CheckIcon as Check,
  ArrowLeftIcon as ArrowLeft,
  TargetIcon as Target,
  CodeIcon as Code,
  WarningOctagonIcon as WarningOctagon,
  GitBranchIcon as GitBranch,
  EnvelopeSimpleIcon as Envelope,
} from "@phosphor-icons/react";
import { getRiskBrief } from "@/lib/api";
import SendBriefModal from "@/components/workspace/SendBriefModal";
import EvidenceSummaryCard from "@/components/metadata/EvidenceSummaryCard";
import DispositionSummaryCard from "@/components/dispositions/DispositionSummaryCard";
import FieldDependencySummaryCard from "@/components/fielddeps/FieldDependencySummaryCard";

const BAND_COLOR = {
  HIGH:   { bg: "#fff1f1", fg: "#a2191f", border: "#ffd7d9" },
  MEDIUM: { bg: "#fff8e1", fg: "#8a6c00", border: "#f1c21b" },
  LOW:    { bg: "#defbe6", fg: "#0e6027", border: "#a7f0ba" },
};

function BandPill({ band }) {
  const c = BAND_COLOR[band] || BAND_COLOR.LOW;
  return (
    <span
      className="ml-mono text-[10px] uppercase tracking-widest px-1.5 py-0.5 border whitespace-nowrap"
      style={{ background: c.bg, color: c.fg, borderColor: c.border }}
      data-testid={`brief-band-${band}`}
    >
      {band}
    </span>
  );
}

function buildMarkdown(brief) {
  // Slide-ready markdown for "Copy as markdown". Keep tables narrow enough
  // that they render in Confluence / GDocs without horizontal scroll.
  const lines = [];
  const title = brief.client_name
    ? `${brief.client_name} — Modernization Risk Brief`
    : `${brief.workspace_name} — Modernization Risk Brief`;
  lines.push(`# ${title}`);
  lines.push("");
  lines.push(`_Generated ${brief.generated_at?.slice(0, 19).replace("T", " ")} UTC_`);
  lines.push("");

  if (brief.sap_packages?.top?.length) {
    lines.push(`## Top stuck SAP packages (${brief.sap_packages.top.length})`);
    lines.push("");
    lines.push("| Band | Package | Score | Objs | Mod-std | LOC | Active exits |");
    lines.push("|------|---------|------:|-----:|--------:|----:|-------------:|");
    for (const p of brief.sap_packages.top) {
      lines.push(
        `| ${p.risk_band} | \`${p.package}\` | ${p.risk_score} | ${p.object_count}`
        + ` | ${p.modified_standard_count || ""} | ${p.total_loc.toLocaleString()}`
        + ` | ${p.active_enhancements || ""} |`,
      );
    }
    lines.push("");
    if (brief.sap_packages.modified_standard_hotspots?.length) {
      lines.push(`### Modified-standard hot-spots`);
      lines.push("");
      lines.push("| Object | Type | Package | LOC |");
      lines.push("|--------|------|---------|----:|");
      for (const o of brief.sap_packages.modified_standard_hotspots) {
        lines.push(`| \`${o.obj_name}\` | ${o.obj_type || "—"} | ${o.package || "—"} | ${(o.loc || 0).toLocaleString()} |`);
      }
      lines.push("");
    }
  }

  if (brief.ibmi_files?.top_files?.length) {
    lines.push(`## Top anchored IBM-i files (${brief.ibmi_files.top_files.length})`);
    lines.push("");
    lines.push("| File | Rules | Programs | Fields | Blast-radius weight |");
    lines.push("|------|------:|---------:|-------:|--------------------:|");
    for (const f of brief.ibmi_files.top_files) {
      lines.push(
        `| \`${f.file}\` | ${f.rule_count} | ${f.program_count}`
        + ` | ${f.field_count} | ${f.weight} |`,
      );
    }
    lines.push("");
    const val = brief.ibmi_files.totals?.validation;
    if (val) {
      lines.push(
        val.validated
          ? `_Rule validation: ${val.validated} validated by a consultant, ${val.proposed} still parser inference (unvalidated), ${val.rejected} rejected and excluded._`
          : `_No business rules have been consultant-validated yet — every rule counted above is parser inference and must be read as a hypothesis._`,
      );
      lines.push("");
    }
    if (brief.ibmi_files.top_fields?.length) {
      lines.push(`### Top business-rule fields`);
      lines.push("");
      lines.push("| Field | Rules | Programs | Weight |");
      lines.push("|-------|------:|---------:|-------:|");
      for (const f of brief.ibmi_files.top_fields) {
        lines.push(`| \`${f.field}\` | ${f.rule_count} | ${f.program_count} | ${f.weight} |`);
      }
      lines.push("");
    }
  }

  return lines.join("\n");
}

function SectionHeader({ Icon, title, subtitle, count }) {
  return (
    <div className="px-3 py-2 border-b border-[var(--ml-border)] flex items-center justify-between gap-3">
      <div className="flex items-center gap-2">
        <Icon size={14} weight="bold" className="text-[var(--ml-primary)]" />
        <span className="ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-primary)]">
          {title}
          {count != null && <span className="ml-1 text-[var(--ml-text-secondary)]">· {count}</span>}
        </span>
      </div>
      {subtitle && (
        <span className="text-xs text-[var(--ml-text-secondary)]">{subtitle}</span>
      )}
    </div>
  );
}

export default function WorkspaceRiskBrief() {
  const { id: workspaceId } = useParams();
  const [brief, setBrief] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [emailOpen, setEmailOpen] = useState(false);

  const fetchBrief = useCallback(async () => {
    setLoading(true);
    try {
      setBrief(await getRiskBrief(workspaceId));
    } finally {
      setLoading(false);
    }
  }, [workspaceId]);

  useEffect(() => { fetchBrief(); }, [fetchBrief]);

  const handleCopy = async () => {
    if (!brief) return;
    try {
      await navigator.clipboard.writeText(buildMarkdown(brief));
      setCopied(true);
      toast.success("Brief copied as markdown — paste straight into your slide.");
      setTimeout(() => setCopied(false), 3000);
    } catch {
      toast.error("Couldn't access clipboard.");
    }
  };

  if (loading) {
    return (
      <div className="max-w-6xl mx-auto px-6 py-16 text-center" data-testid="brief-loading">
        <Spinner size={20} className="animate-spin inline mr-2" />
        Loading risk brief…
      </div>
    );
  }
  if (!brief) return null;

  return (
    <div className="max-w-6xl mx-auto px-6 py-8 space-y-4" data-testid="risk-brief-page">
      <div className="flex items-center justify-between">
        <Link
          to={`/workspaces/${workspaceId}`}
          className="ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)] hover:text-[var(--ml-text-primary)] inline-flex items-center gap-1"
          data-testid="brief-back"
        >
          <ArrowLeft size={11} weight="bold" /> Workspace
        </Link>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setEmailOpen(true)}
            disabled={!brief.has_signal}
            data-testid="brief-send-email"
            className="ml-btn-secondary text-xs inline-flex items-center gap-1.5"
            title="Send the brief as an email to a teammate or external stakeholder."
          >
            <Envelope size={11} weight="bold" />
            Send by email
          </button>
          <button
            type="button"
            onClick={handleCopy}
            disabled={!brief.has_signal}
            data-testid="brief-copy-md"
            className="ml-btn-secondary text-xs inline-flex items-center gap-1.5"
            title="Copy the entire brief as Markdown — drop straight into a slide or Confluence page."
          >
            {copied ? <Check size={11} weight="bold" /> : <ClipboardText size={11} weight="bold" />}
            {copied ? "Copied" : "Copy as Markdown"}
          </button>
        </div>
      </div>

      <div>
        <h1 className="text-2xl font-semibold tracking-tight flex items-center gap-2">
          <Target size={20} weight="duotone" /> Workspace Risk Brief
        </h1>
        <div className="text-sm text-[var(--ml-text-secondary)] mt-1">
          <strong className="text-[var(--ml-text-primary)]">
            {brief.client_name || brief.workspace_name}
          </strong>
          {brief.client_name && (
            <span className="text-[var(--ml-text-secondary)]"> · {brief.workspace_name}</span>
          )}
          <span className="ml-2 ml-mono text-[10px] uppercase tracking-widest">
            Generated {brief.generated_at?.slice(0, 19).replace("T", " ")} UTC
          </span>
        </div>
      </div>

      {!brief.has_signal && (
        <div className="ml-card p-10 text-center" data-testid="brief-empty">
          <Target size={36} weight="duotone" className="text-[var(--ml-text-secondary)] inline mb-3" />
          <div className="text-base font-semibold mb-1">Nothing to focus on yet</div>
          <p className="text-sm text-[var(--ml-text-secondary)] max-w-md mx-auto">
            Import a TADIR or SMOD/CMOD extract on the
            <Link to={`/workspaces/${workspaceId}/sap-ecc`} className="text-[var(--ml-primary)] hover:underline mx-1">
              SAP ECC
            </Link>
            tab, OR parse an RPG program from the
            <Link to={`/workspaces/${workspaceId}/metadata/source-parser`} className="text-[var(--ml-primary)] hover:underline mx-1">
              Source Parser
            </Link>
            tab — this brief auto-populates as soon as either side has data.
          </p>
        </div>
      )}

      {brief.sap_packages?.top?.length > 0 && (
        <div className="ml-card overflow-x-auto" data-testid="brief-sap-section">
          <SectionHeader
            Icon={ChartBar}
            title="Top stuck SAP packages"
            count={brief.sap_packages.top.length}
            subtitle="Ranked by heat-map risk_score (mod-std × 100 + LOC/100 + recency + enhancement_weight)"
          />
          <table className="w-full text-sm">
            <thead className="ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)] bg-[var(--ml-surface)]/50">
              <tr>
                <th className="px-3 py-2 text-left">Band</th>
                <th className="px-3 py-2 text-left">Package</th>
                <th className="px-3 py-2 text-right">Score</th>
                <th className="px-3 py-2 text-right">Objects</th>
                <th className="px-3 py-2 text-right">Mod-std</th>
                <th className="px-3 py-2 text-right">LOC</th>
                <th className="px-3 py-2 text-right">Active exits</th>
              </tr>
            </thead>
            <tbody>
              {brief.sap_packages.top.map((p) => (
                <tr
                  key={p.package}
                  data-testid={`brief-sap-row-${p.package.replace(/[/\s()]/g, "_")}`}
                  className="border-b last:border-b-0 border-[var(--ml-border)] hover:bg-[var(--ml-surface)]/30"
                >
                  <td className="px-3 py-2"><BandPill band={p.risk_band} /></td>
                  <td className="px-3 py-2 ml-mono font-semibold">{p.package}</td>
                  <td className="px-3 py-2 ml-mono text-right tabular-nums">{p.risk_score.toLocaleString()}</td>
                  <td className="px-3 py-2 ml-mono text-right tabular-nums">{p.object_count}</td>
                  <td className="px-3 py-2 ml-mono text-right tabular-nums text-[#a2191f] font-semibold">
                    {p.modified_standard_count > 0 ? (
                      <span className="inline-flex items-center gap-0.5">
                        <WarningOctagon size={10} weight="bold" /> {p.modified_standard_count}
                      </span>
                    ) : "—"}
                  </td>
                  <td className="px-3 py-2 ml-mono text-right tabular-nums">{p.total_loc.toLocaleString()}</td>
                  <td className="px-3 py-2 ml-mono text-right tabular-nums text-[#6929c4]">
                    {p.active_enhancements > 0 ? p.active_enhancements : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {brief.sap_packages?.modified_standard_hotspots?.length > 0 && (
        <div className="ml-card overflow-x-auto" data-testid="brief-modstd-section">
          <SectionHeader
            Icon={WarningOctagon}
            title="Modified-standard hot-spots"
            count={brief.sap_packages.modified_standard_hotspots.length}
            subtitle="SPDD/SPAU conversion blockers — must be cleared before S/4HANA cutover."
          />
          <table className="w-full text-sm">
            <thead className="ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)] bg-[var(--ml-surface)]/50">
              <tr>
                <th className="px-3 py-2 text-left">Object</th>
                <th className="px-3 py-2 text-left">Type</th>
                <th className="px-3 py-2 text-left">Package</th>
                <th className="px-3 py-2 text-right">LOC</th>
              </tr>
            </thead>
            <tbody>
              {brief.sap_packages.modified_standard_hotspots.map((o) => (
                <tr
                  key={`${o.package}-${o.obj_name}`}
                  data-testid={`brief-modstd-row-${o.obj_name}`}
                  className="border-b last:border-b-0 border-[var(--ml-border)]"
                >
                  <td className="px-3 py-2 ml-mono font-semibold">{o.obj_name}</td>
                  <td className="px-3 py-2 ml-mono text-xs">{o.obj_type || "—"}</td>
                  <td className="px-3 py-2 ml-mono text-xs">{o.package || "—"}</td>
                  <td className="px-3 py-2 ml-mono text-right tabular-nums">{(o.loc || 0).toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {brief.ibmi_files?.top_files?.length > 0 && (
        <div className="ml-card overflow-x-auto" data-testid="brief-ibmi-section">
          <SectionHeader
            Icon={Code}
            title="Top anchored IBM-i files"
            count={brief.ibmi_files.top_files.length}
            subtitle="Ranked by blast-radius weight (rule_count × program_count). Touching these files = high downstream impact."
          />
          {brief.ibmi_files.totals?.validation && (
            <div
              className="px-3 py-2 text-xs border-b border-[var(--ml-border)]"
              style={brief.ibmi_files.totals.validation.validated
                ? { background: "#defbe6", color: "#0e6027" }
                : { background: "#f6f2ff", color: "#6929c4" }}
              data-testid="brief-ibmi-validation"
            >
              {brief.ibmi_files.totals.validation.validated
                ? <><strong>{brief.ibmi_files.totals.validation.validated} validated</strong> by a consultant · {brief.ibmi_files.totals.validation.proposed} still parser inference · {brief.ibmi_files.totals.validation.rejected} rejected (excluded)</>
                : <><strong>No rules validated yet</strong> — all {brief.ibmi_files.totals.validation.proposed} counted rules are parser inference; read as hypotheses.</>}
            </div>
          )}
          <table className="w-full text-sm">
            <thead className="ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)] bg-[var(--ml-surface)]/50">
              <tr>
                <th className="px-3 py-2 text-left">File</th>
                <th className="px-3 py-2 text-right">Rules</th>
                <th className="px-3 py-2 text-right">Programs</th>
                <th className="px-3 py-2 text-right">Fields</th>
                <th className="px-3 py-2 text-right">Weight</th>
                <th className="px-3 py-2 text-left">Touched by</th>
              </tr>
            </thead>
            <tbody>
              {brief.ibmi_files.top_files.map((f) => (
                <tr
                  key={f.file}
                  data-testid={`brief-ibmi-row-${f.file}`}
                  className="border-b last:border-b-0 border-[var(--ml-border)] hover:bg-[var(--ml-surface)]/30"
                >
                  <td className="px-3 py-2 ml-mono font-semibold">{f.file}</td>
                  <td className="px-3 py-2 ml-mono text-right tabular-nums">{f.rule_count}</td>
                  <td className="px-3 py-2 ml-mono text-right tabular-nums">{f.program_count}</td>
                  <td className="px-3 py-2 ml-mono text-right tabular-nums text-[var(--ml-text-secondary)]">{f.field_count}</td>
                  <td className="px-3 py-2 ml-mono text-right tabular-nums font-semibold text-[var(--ml-primary)]">{f.weight}</td>
                  <td className="px-3 py-2 ml-mono text-xs text-[var(--ml-text-secondary)]">
                    {(f.programs || []).slice(0, 3).join(", ")}
                    {(f.programs || []).length > 3 && (
                      <span className="text-[var(--ml-text-secondary)]"> +{f.programs.length - 3}</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {brief.ibmi_files?.top_fields?.length > 0 && (
        <div className="ml-card overflow-x-auto" data-testid="brief-ibmi-fields-section">
          <SectionHeader
            Icon={GitBranch}
            title="Top business-rule fields"
            count={brief.ibmi_files.top_fields.length}
            subtitle="Fields named in the most decision points — change-radar candidates."
          />
          <table className="w-full text-sm">
            <thead className="ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)] bg-[var(--ml-surface)]/50">
              <tr>
                <th className="px-3 py-2 text-left">Field</th>
                <th className="px-3 py-2 text-right">Rules</th>
                <th className="px-3 py-2 text-right">Programs</th>
                <th className="px-3 py-2 text-right">Weight</th>
              </tr>
            </thead>
            <tbody>
              {brief.ibmi_files.top_fields.map((f) => (
                <tr
                  key={f.field}
                  data-testid={`brief-ibmi-field-row-${f.field}`}
                  className="border-b last:border-b-0 border-[var(--ml-border)]"
                >
                  <td className="px-3 py-2 ml-mono font-semibold">{f.field}</td>
                  <td className="px-3 py-2 ml-mono text-right tabular-nums">{f.rule_count}</td>
                  <td className="px-3 py-2 ml-mono text-right tabular-nums">{f.program_count}</td>
                  <td className="px-3 py-2 ml-mono text-right tabular-nums font-semibold text-[var(--ml-primary)]">{f.weight}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <FieldDependencySummaryCard workspaceId={workspaceId} summary={brief.field_dependencies} />
      <DispositionSummaryCard workspaceId={workspaceId} summary={brief.dispositions} />
      <EvidenceSummaryCard workspaceId={workspaceId} evidence={brief.evidence_summary} />

      <SendBriefModal
        workspaceId={workspaceId}
        workspaceName={brief.workspace_name}
        clientName={brief.client_name}
        open={emailOpen}
        onClose={() => setEmailOpen(false)}
      />
    </div>
  );
}
